import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../store/slices/authSlice";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, LogOut, X } from "lucide-react";

const ONE_DAY_MS = 60 * 60 * 1000; // 1 hour
const WARNING_TIME = 5 * 60 * 1000; // 5 minutes warning before logout

const SessionWarnings = ({ lastActivity, timeout = ONE_DAY_MS }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(WARNING_TIME);
  const [warningTriggeredAt, setWarningTriggeredAt] = useState(null);
  const warningAudioRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!lastActivity) return;

    const checkTimeout = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const timeUntilTimeout = timeout - timeSinceActivity;

      // If we're within warning period
      if (timeUntilTimeout <= WARNING_TIME && timeUntilTimeout > 0) {
        if (!showWarning) {
          // First time entering warning mode
          setWarningTriggeredAt(now);
          setShowWarning(true);
        }
        setTimeLeft(timeUntilTimeout);
      }
      // If timeout reached
      else if (timeUntilTimeout <= 0) {
        handleLogout();
      }
      // If we're out of warning period (user extended session)
      else if (showWarning && timeUntilTimeout > WARNING_TIME) {
        setShowWarning(false);
        setWarningTriggeredAt(null);
      }
    };

    const interval = setInterval(checkTimeout, 1000);
    checkTimeout(); // Initial check

    return () => clearInterval(interval);
  }, [lastActivity, timeout, showWarning]);

  useEffect(() => {
    if (showWarning && warningAudioRef.current) {
      warningAudioRef.current.currentTime = 0;
      warningAudioRef.current.play().catch((e) => {});
    } else if (warningAudioRef.current) {
      warningAudioRef.current.pause();
      warningAudioRef.current.currentTime = 0;
    }
  }, [showWarning]);

  const handleLogout = async () => {
    if (warningAudioRef.current) {
      warningAudioRef.current.pause();
      warningAudioRef.current.currentTime = 0;
    }
    setShowWarning(false);
    setWarningTriggeredAt(null);

    try {
      await dispatch(logout()).unwrap();
      navigate("/login");
    } catch (error) {
      // Force logout on error
      localStorage.clear();
      sessionStorage.clear();
      navigate("/login");
    }
  };

  const handleStayLoggedIn = () => {
    if (warningAudioRef.current) {
      warningAudioRef.current.pause();
      warningAudioRef.current.currentTime = 0;
    }
    setShowWarning(false);
    setWarningTriggeredAt(null);

    // Dispatch an event to update last activity
    const activityEvent = new CustomEvent("userActivity", {
      detail: { timestamp: Date.now() },
    });
    window.dispatchEvent(activityEvent);
  };

  const formatTime = (ms) => {
    if (ms <= 0) return "0:00";
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Calculate percentage for progress bar
  const getProgressPercentage = () => {
    if (!warningTriggeredAt) return 100;
    const elapsed = Date.now() - warningTriggeredAt;
    return Math.max(0, Math.min(100, (elapsed / WARNING_TIME) * 100));
  };

  return (
    <AnimatePresence>
      {showWarning && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
            onClick={handleStayLoggedIn}
          />

          {/* Warning Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-x-4 top-4 mx-auto max-w-md z-[101]"
            role="dialog"
            aria-labelledby="session-warning-title"
            aria-describedby="session-warning-description"
          >
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 rounded-2xl p-6 border-2 border-amber-200 dark:border-amber-700 shadow-2xl">
              {/* Audio Element */}
              <audio
                ref={warningAudioRef}
                loop
                className="hidden"
                preload="metadata"
              >
                <source src="/sounds/warning-alert.mp3" type="audio/mpeg" />
                <source src="/sounds/warning-alert.ogg" type="audio/ogg" />
                Your browser does not support the audio element.
              </audio>

              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-800 animate-pulse">
                    <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3
                      id="session-warning-title"
                      className="font-bold text-lg text-amber-800 dark:text-amber-300"
                    >
                      جلسة على وشك الانتهاء
                    </h3>
                    <p
                      id="session-warning-description"
                      className="text-sm text-amber-700 dark:text-amber-400"
                    >
                      سيتم تسجيل خروجك تلقائياً بسبب عدم النشاط
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleStayLoggedIn}
                  className="p-1 hover:bg-amber-200 dark:hover:bg-amber-800 rounded-lg transition-colors"
                  aria-label="إغلاق التحذير"
                >
                  <X className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </button>
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    الوقت المتبقي:
                  </span>
                  <span className="text-lg font-bold text-amber-700 dark:text-amber-400">
                    {formatTime(timeLeft)}
                  </span>
                </div>
                <div className="h-2 bg-amber-200 dark:bg-amber-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                    initial={{ width: "0%" }}
                    animate={{ width: `${getProgressPercentage()}%` }}
                    transition={{ duration: 1, ease: "linear" }}
                  />
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-500 mt-2 text-center">
                  سيتم تسجيل خروجك بعد {Math.floor(WARNING_TIME / 60000)} دقائق
                </p>
              </div>

              <div className="flex gap-3">
                <motion.button
                  onClick={handleLogout}
                  className="flex-1 py-3 rounded-xl font-medium border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <LogOut className="w-4 h-4" />
                  تسجيل الخروج الآن
                </motion.button>
                <motion.button
                  onClick={handleStayLoggedIn}
                  className="flex-1 py-3 rounded-xl font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  البقاء مسجلاً
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SessionWarnings;
